// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Admin panel  (catalog validation for /admin/products/:id)
// Pure checks over ONE catalog entry (data/seafood + data/packs + data/cuts) and, when the catch landed today, its Daily Catch stock. Each check says
// what is being verified, whether it holds and — when it doesn't — what to fix. These are the rules the customer site relies on (a product page URL,
// a positive per-kg price, linear pack prices, a credited photo, at least one cut, non-negative prep fees…), NOT invented compliance checks.
// Also used by the new-product studio (pages/admin/AdminProductNew) to judge a draft before its entry is pasted into the catalog.
// Neighbours: pages/admin/AdminProductDetail, data/seafood|packs|cuts, utils/drop (Landing).
// ─────────────────────────────────────────────────────────────────────────────
import { cutsFor, type CutOption } from "../data/cuts";
import { PACK_SIZES, packPrice } from "../data/packs";
import { SEAFOOD_CATEGORIES, type SeafoodItem } from "../data/seafood";
import type { Landing } from "./drop";
import { parsePriceToNumber } from "./priceFormat";

export interface ProductCheck {
  id: string;
  label: string;
  ok: boolean;
  /** What was found (when ok) or what to fix (when not). */
  detail: string;
}

const SLUG = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const BINOMIAL = /^[A-Z][a-z]+ [a-z]+/;

export interface CheckOptions {
  /** The cuts to judge instead of the catalog's own (a draft that isn't in data/cuts.ts yet). */
  cuts?: CutOption[];
  /** Ids already in the catalog: a draft must not reuse one. */
  takenIds?: string[];
}

export function checkProduct(item: SeafoodItem, landings: Landing[] = [], options: CheckOptions = {}): ProductCheck[] {
  const kg = PACK_SIZES[PACK_SIZES.length - 1];
  const half = PACK_SIZES[0];
  const perKg = parsePriceToNumber(item.price);
  const cuts = options.cuts ?? cutsFor(item);
  const cutIds = new Set(cuts.map((c) => c.id));
  const badFee = cuts.find((c) => !Number.isInteger(c.prepFeePerKg) || c.prepFeePerKg < 0);
  const stockBad = landings.find((l) => l.packsLeft < 0 || l.packsLeft > l.packsTotal);
  const checks: ProductCheck[] = [
    { id: "name", label: "Has a name customers can read", ok: item.name.trim().length >= 3, detail: item.name.trim().length >= 3 ? item.name : "The name is empty or too short." },
    { id: "slug", label: "Id works in the product page address (/menu/<id>)", ok: SLUG.test(item.id), detail: SLUG.test(item.id) ? `/menu/${item.id}` : "Use lowercase letters, digits and single hyphens only." },
    { id: "scientific", label: "Scientific name is a genus and species", ok: BINOMIAL.test(item.scientificName), detail: BINOMIAL.test(item.scientificName) ? item.scientificName : "Write it like “Pampus argenteus”." },
    { id: "category", label: "Category is one the menu knows", ok: SEAFOOD_CATEGORIES.includes(item.category), detail: SEAFOOD_CATEGORIES.includes(item.category) ? item.category : `Use one of: ${SEAFOOD_CATEGORIES.join(", ")}.` },
    { id: "description", label: "Has a description (20+ characters)", ok: item.description.trim().length >= 20, detail: item.description.trim().length >= 20 ? `${item.description.trim().length} characters` : "Add a sentence about the catch." },
    { id: "price", label: "Price per kg is a positive whole-rupee amount", ok: Number.isInteger(perKg) && perKg > 0, detail: Number.isInteger(perKg) && perKg > 0 ? `₹${perKg} per kg` : `“${item.price}” isn't a positive whole number of rupees.` },
    { id: "packs", label: "Pack prices agree (500 g is half of 1 kg, to the rupee)", ok: Math.abs(packPrice(item, half) * 2 - packPrice(item, kg)) <= 1, detail: `500 g ₹${packPrice(item, half)} · 1 kg ₹${packPrice(item, kg)}` },
    { id: "photo", label: "Has a photo with a credit", ok: /^https:\/\//.test(item.image) && item.credit.trim().length > 0, detail: item.credit.trim().length > 0 ? item.credit : "Add the photographer and licence." },
    { id: "cuts", label: "Offers at least one cut, with no duplicates", ok: cuts.length > 0 && cutIds.size === cuts.length, detail: cuts.length > 0 ? `${cuts.length} ${cuts.length === 1 ? "cut" : "cuts"}: ${cuts.map((c) => c.name).join(", ")}` : "Choose at least one cut." },
    { id: "fees", label: "Prep fees are whole rupees per kg, never negative", ok: !badFee, detail: badFee ? `${badFee.name} has a fee of ${badFee.prepFeePerKg}.` : "All prep fees are valid." },
  ];
  if (options.takenIds) {
    const taken = options.takenIds.includes(item.id);
    checks.splice(2, 0, { id: "unique", label: "Id is not already used by another product", ok: !taken, detail: taken ? `“${item.id}” is already in the catalog.` : "Unique" });
  }
  if (landings.length > 0) {
    checks.push({ id: "stock", label: "Today's stock is sensible (0 ≤ packs left ≤ packs total)", ok: !stockBad, detail: stockBad ? `A landing shows ${stockBad.packsLeft} of ${stockBad.packsTotal} packs.` : "Daily Catch numbers agree." });
  }
  return checks;
}
